import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Calendar, Globe, Loader2, Plus, Trophy, Users, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface CreateAffiliateEventPayload {
  name: string;
  description?: string;
  event_date: string;
  max_participants?: number;
  visibility: 'internal' | 'public';
}

export default function AffiliateEvents() {
  const { hasPermission } = usePermissions();
  const canCreateEvents = hasPermission('create_events');
  const affiliatesApi = useAffiliatesApi();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'public'>('internal');
  const [viewEventId, setViewEventId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const createSubmission = useSubmissionLock<CreateAffiliateEventPayload>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['affiliate-events'],
    queryFn: async () => {
      const response = await affiliatesApi.getEvents();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const createEventMutation = useMutation({
    mutationFn: async ({
      eventData,
      idempotencyKey,
    }: {
      eventData: {
        name: string;
        description?: string;
        event_date: string;
        max_participants?: number;
        visibility?: 'internal' | 'public';
      };
      idempotencyKey: string;
    }) => {
      const response = await affiliatesApi.createEvent(eventData, idempotencyKey);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      createSubmission.reset();
      toast.success('Event created successfully!');
      setShowCreateModal(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['affiliate-events'] });
    },
    onError: (error) => {
      createSubmission.release();
      toast.error(error instanceof Error ? error.message : 'Failed to create event');
    },
  });

  const { data: eventDetailData, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['affiliate-event-detail', viewEventId],
    queryFn: async () => {
      if (!viewEventId) return null;
      const response = await affiliatesApi.getEventDetails(viewEventId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!viewEventId,
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const response = await affiliatesApi.deleteEvent(eventId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (result) => {
      const msg = result?.status === 'cancelled' ? 'Event cancelled' : 'Event deleted';
      toast.success(msg);
      setConfirmCancelId(null);
      setViewEventId(null);
      queryClient.invalidateQueries({ queryKey: ['affiliate-events'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel event');
    },
  });

  const resetForm = () => {
    setEventName('');
    setEventDescription('');
    setEventDate('');
    setMaxParticipants('');
    setVisibility('internal');
  };

  const handleCreateEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventName || !eventDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const eventData = {
      name: eventName,
      description: eventDescription || undefined,
      event_date: eventDate,
      max_participants: maxParticipants ? Number.parseInt(maxParticipants, 10) : undefined,
      visibility,
    };
    const attempt = createSubmission.begin(eventData);
    if (!attempt) return;

    createEventMutation.mutate({
      idempotencyKey: attempt.idempotencyKey,
      eventData: attempt.payload,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEventStatus = (eventDate: string) => {
    const date = new Date(eventDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date < today) return 'past';
    if (date.getTime() === today.getTime()) return 'today';
    return 'upcoming';
  };

  const statusColors: Record<string, string> = {
    past: 'bg-muted text-muted-foreground border-muted',
    today: 'bg-green-500/10 text-green-500 border-green-500/20',
    upcoming: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  };

  const statusLabels: Record<string, string> = {
    past: 'Completed',
    today: 'Today',
    upcoming: 'Upcoming',
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load events</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  const events = data?.events || [];
  const upcomingEvents = events.filter((e) => getEventStatus(e.event_date) !== 'past');
  const pastEvents = events.filter((e) => getEventStatus(e.event_date) === 'past');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Events</h1>
          <p className="mt-1 text-muted-foreground">
            Create and manage gym events and competitions.
          </p>
        </div>
        {canCreateEvents && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        )}
      </div>

      {/* Upcoming Events */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Upcoming Events</h2>
        {upcomingEvents.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <Trophy className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-1">No upcoming events</h3>
                <p className="text-muted-foreground max-w-md mb-4">
                  Create an event to let members sign up for competitions.
                </p>
                {canCreateEvents && (
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Event
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {upcomingEvents.map((event, index) => {
              const status = getEventStatus(event.event_date);
              return (
                <Card
                  key={event.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{event.name}</CardTitle>
                      <div className="flex items-center gap-1">
                        {(event as { visibility?: string }).visibility === 'public' && (
                          <Badge
                            variant="outline"
                            className="bg-blue-500/10 text-blue-400 border-blue-500/20"
                          >
                            <Globe className="h-3 w-3 mr-1" />
                            Public
                          </Badge>
                        )}
                        <Badge variant="outline" className={statusColors[status]}>
                          {statusLabels[status]}
                        </Badge>
                      </div>
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {formatDate(event.event_date)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm mb-3">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="h-4 w-4" />
                        {event.participant_count || 0}
                        {event.max_participants && ` / ${event.max_participants}`} registered
                      </div>
                      {event.max_participants &&
                        event.participant_count >= event.max_participants && (
                          <Badge variant="destructive">Full</Badge>
                        )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setViewEventId(event.id)}
                    >
                      <Users className="h-3 w-3 mr-2" />
                      View Participants
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 text-muted-foreground">Past Events</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pastEvents.map((event) => (
              <Card key={event.id} className="opacity-60">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{event.name}</CardTitle>
                    <Badge variant="outline" className={statusColors.past}>
                      Completed
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(event.event_date)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    {event.participant_count || 0} participated
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {viewEventId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in max-h-[80vh] flex flex-col">
            <CardHeader className="shrink-0">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>{eventDetailData?.event?.name || 'Event'}</CardTitle>
                  <CardDescription>
                    {isLoadingDetail ? (
                      'Loading...'
                    ) : (
                      <>
                        {eventDetailData?.participants?.length ?? 0} registered
                        {eventDetailData?.event?.max_participants
                          ? ` / ${eventDetailData.event.max_participants} max`
                          : ''}
                      </>
                    )}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setViewEventId(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              {isLoadingDetail ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : !eventDetailData?.participants?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No registrations yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {eventDetailData.participants.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div>
                        <p className="font-medium text-sm">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.email}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.signed_up_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="p-6 pt-0 shrink-0">
              {canCreateEvents && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => {
                    setConfirmCancelId(viewEventId);
                  }}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Cancel Event
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Confirm Cancel Dialog */}
      {confirmCancelId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-sm mx-4 animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Cancel Event?
              </CardTitle>
              <CardDescription>
                This action cannot be undone. Registered participants will not be automatically
                notified.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1"
                  disabled={deleteEventMutation.isPending}
                  onClick={() => deleteEventMutation.mutate(confirmCancelId)}
                >
                  {deleteEventMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Yes, Cancel Event'
                  )}
                </Button>
                <Button variant="outline" onClick={() => setConfirmCancelId(null)}>
                  Keep
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Event Modal */}
      {canCreateEvents && showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Create Event</CardTitle>
                  <CardDescription>Set up a new gym event or competition.</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={createSubmission.isLocked}
                  onClick={() => {
                    createSubmission.reset();
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="event-name">Event Name *</Label>
                  <Input
                    id="event-name"
                    placeholder="Friday Night Throwdown"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-date">Event Date *</Label>
                  <Input
                    id="event-date"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-description">Description (optional)</Label>
                  <textarea
                    id="event-description"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Details about the event..."
                    value={eventDescription}
                    onChange={(e) => setEventDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-participants">Max Participants (optional)</Label>
                  <Input
                    id="max-participants"
                    type="number"
                    placeholder="Leave empty for unlimited"
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(e.target.value)}
                    min="1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-visibility">Visibility</Label>
                  <Select
                    id="event-visibility"
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as 'internal' | 'public')}
                    options={[
                      { value: 'internal', label: 'Internal (members only)' },
                      { value: 'public', label: 'Public' },
                    ]}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" disabled={createSubmission.isLocked}>
                    {createSubmission.isLocked ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Event
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={createSubmission.isLocked}
                    onClick={() => {
                      createSubmission.reset();
                      setShowCreateModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
